import { useEffect, useRef, useState, type ReactNode } from "react";

type Detector = { detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>> };
type DetectorConstructor = new () => Detector;
type ScannerControls = { stop(): void };

/** Uses the native decoder when available and ZXing everywhere else. */
export function CameraBarcodeModal({
  stream,
  initialError = "",
  onDetected,
  onClose,
  title = "Scan barcode",
  hint = "Hold the barcode inside the frame",
  footer,
}: {
  stream: MediaStream | null;
  initialError?: string;
  onDetected: (barcode: string) => void;
  onClose: () => void;
  title?: string;
  hint?: string;
  footer?: ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState(initialError);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let controls: ScannerControls | null = null;
    let timer = 0;
    let cancelled = false;
    let accepted = false;

    const accept = (rawValue: string) => {
      const barcode = rawValue.trim();
      if (!barcode || cancelled || accepted) return;
      accepted = true;
      onDetected(barcode);
      onClose();
    };

    const startSoftwareDecoder = async (video: HTMLVideoElement) => {
      // Kept out of the initial POS bundle; only browsers without a native
      // decoder download it, and the PWA caches the resulting chunk.
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      if (cancelled) return;
      const reader = new BrowserMultiFormatReader(undefined, { delayBetweenScanAttempts: 120, delayBetweenScanSuccess: 500 });
      if (!stream) return;
      controls = await reader.decodeFromStream(
        stream,
        video,
        (result) => {
          if (result) accept(result.getText());
        },
      );
      if (cancelled) controls.stop();
      else setReady(true);
    };

    const start = async () => {
      if (!stream) return;
      const video = videoRef.current;
      if (!video) return;
      const DetectorApi = (window as unknown as { BarcodeDetector?: DetectorConstructor }).BarcodeDetector;
      if (!DetectorApi) {
        await startSoftwareDecoder(video);
        return;
      }

      // Native decoding is faster and uses less battery, but is only an
      // optimization—the software path above covers Safari and Firefox.
      const detector = new DetectorApi();
      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      video.srcObject = stream;
      await video.play();
      setReady(true);
      const scan = async () => {
        if (cancelled || accepted) return;
        try {
          const found = await detector.detect(video);
          if (found[0]?.rawValue) accept(found[0].rawValue);
        } catch {
          // Frames routinely fail while autofocus is settling.
        }
        timer = window.setTimeout(scan, 160);
      };
      void scan();
    };

    void start().catch((cause: unknown) => {
      if (cancelled) return;
      const denied = cause instanceof DOMException && (cause.name === "NotAllowedError" || cause.name === "SecurityError");
      setError(denied ? "Camera access is blocked. Allow camera access in browser settings, then try again." : "The camera could not start. Close other apps using it and try again.");
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controls?.stop();
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [onClose, onDetected, stream]);

  return (
    <div className="pos-dim" onClick={onClose}>
      <section role="dialog" aria-modal="true" aria-label={title} className="pos-dialog pos-scan" onClick={(event) => event.stopPropagation()}>
        <div className="pos-dialog-head">
          <h2>{title}</h2>
          <button className="pos-sq-btn big" onClick={onClose} aria-label="Close">
            <span className="ms" aria-hidden="true">close</span>
          </button>
        </div>
        {error ? (
          <div role="alert" className="pos-alert bad">
            <span className="ms" aria-hidden="true">no_photography</span>
            {error}
          </div>
        ) : (
          <div className="pos-scan-view">
            <video ref={videoRef} playsInline muted />
            <span className="pos-scan-frame" />
            <span className="pos-scan-line" />
            {!ready && <div aria-live="polite" className="pos-scan-wait">Starting camera…</div>}
          </div>
        )}
        {!error && <p className="pos-scan-hint">{hint}</p>}
        {footer}
      </section>
    </div>
  );
}
